import Button from "atoms/buttons/button";
import Loader from "atoms/loaders/loader";
import Modal from "molecules/modals/modal";
import Paragraph from "atoms/typography/paragraph";
import React from "react";
import { ButtonStyles } from "atoms/constants/button-styles";
import { ModalTypes } from "molecules/constants/modal-types";
import { ToastManager } from "utilities/toast/toast-manager";
import { useLocalization } from "utilities/hooks/use-localization";
import { useManagePageAccess } from "organisms/modals/page-access-modal/hooks/use-manage-page-access";
import { Breakpoints } from "utilities/enumerations/breakpoints";
import useBreakpoint, {
    BreakpointComparer,
} from "utilities/hooks/use-breakpoint";
import { ModalCloseButtonStyle } from "molecules/constants/modal-close-button-style";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { IconSizes } from "atoms/constants/icon-sizes";
import Icon from "atoms/icons/icon";
import Heading from "atoms/typography/heading";
import { Icons } from "atoms/constants/icons";

interface GlobalPageShareLinkModalProps {
    isOpen: boolean;
    onClose: () => void;
    pageId: number;
}

const CSS_CLASS_NAME: string = "c-global-page-share-link-modal";

export const GlobalPageShareLinkModal: React.FC<
    GlobalPageShareLinkModalProps
> = ({
    isOpen,
    onClose,
    pageId,
}: GlobalPageShareLinkModalProps): JSX.Element => {
    const { t } = useLocalization();
    const { publicPageLink, loading } = useManagePageAccess(pageId);

    const handleCopyPageLinkClick = () => {
        navigator.clipboard.writeText(publicPageLink);
        ToastManager.success(t("textCopied"));
    };

    const isMobile = useBreakpoint(
        Breakpoints.SmallTablet,
        BreakpointComparer.MaxWidth
    );
    const modalType = isMobile ? ModalTypes.Bottom : ModalTypes.Base;

    return (
        <Modal
            closeButtonStyle={ModalCloseButtonStyle.Hidden}
            closeDialog={onClose}
            isVisible={isOpen}
            cssClassName={CSS_CLASS_NAME}
            type={modalType}
            label={""}>
            {loading ? (
                <Loader />
            ) : (
                <div className={`${CSS_CLASS_NAME}__container`}>
                    <Heading cssClassName={`${CSS_CLASS_NAME}__heading`}>
                        {t("notebooks-shareable-link")}
                        <Button
                            accessibleText={t("close")}
                            cssClassName={"-modal-close -icon"}
                            onClick={onClose}
                            size={ButtonSizes.Medium}
                            style={ButtonStyles.None}>
                            <Icon type={Icons.Close} size={IconSizes.Large} />
                        </Button>
                    </Heading>
                    <div className={`${CSS_CLASS_NAME}__content`}>
                        <div
                            className={`${CSS_CLASS_NAME}__public-access-fields`}>
                            <div
                                className={`${CSS_CLASS_NAME}__public-access-link`}>
                                <div
                                    className={`${CSS_CLASS_NAME}__public-access-url`}>
                                    {publicPageLink}
                                </div>
                                <Button
                                    cssClassName={`${CSS_CLASS_NAME}__copyButton`}
                                    onClick={handleCopyPageLinkClick}
                                    style={ButtonStyles.Tertiary}>
                                    {t("copyLink")}
                                </Button>
                            </div>
                        </div>
                    </div>
                    <div className={`${CSS_CLASS_NAME}__footer`}>
                        <Paragraph
                            cssClassName={`${CSS_CLASS_NAME}__public-access-note`}>
                            {t("managePageAccess-public-access-info")}
                        </Paragraph>
                        <Button
                            accessibleText={t("done")}
                            cssClassName={`${CSS_CLASS_NAME}__done-button`}
                            onClick={onClose}
                            style={ButtonStyles.Primary}>
                            {t("done")}
                        </Button>
                    </div>
                </div>
            )}
        </Modal>
    );
};
